import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { organizerMeApi, zocoCanjearApi } from "../../api/api";
import { LOGIN_SUCCESS, LOGOUT_SUCCESS } from "../../redux/actions/action";
import { ORIGEN_ZOCO, rutaDeVuelta, urlDelPortal } from "../../helpers/zoco";
import logoZoco from "../../assets/image/logo.e3c0b2196cc23f84f67a.png";
import "./EntrarDesdeZoco.css";

/*
  La vuelta del portal de Zoco (sesión 6 de instrucSesiones). Es la pantalla a la que apunta
  `Productos:Tickets:Url` en Zoco: el portal manda a la persona acá con `?codigo=`, un código de
  un solo uso que vive sesenta segundos y ya trae adentro el negocio que eligió. Se canjea apenas
  se carga, contra el back de Tickets (POST /api/zoco/canjear), que es el único que tiene la
  credencial de servicio. El código no se guarda en ningún lado.

  Adónde sigue lo decide el back, no el front:
    - sin negocio      → es público que vino a comprar entradas: al catálogo;
    - con negocio      → se pregunta /api/organizer/me. Si entra, al panel. Si no —un empleado,
                         al que el back deja como USER hasta la guarda por permiso (fase 6, B.4)—
                         se le dice que su rol todavía no entra al panel.
  Preguntarle al back en vez de leer `titular.tipo` es a propósito: la regla de quién opera el
  panel (dueño, con el espejo del negocio existente, de su titular y activo) vive en
  middleware/verifyUserToken.js del back, y una copia acá se desincronizaría.
*/

const Marco = ({ children }) => (
  <main className="entrar-zoco">
    <div className="entrar-zoco__card">
      <h1 className="entrar-zoco__marca">
        <img className="entrar-zoco__logo" src={logoZoco} alt="ZOCO Tickets" />
        <span className="entrar-zoco__producto" aria-hidden="true">
          Tickets
        </span>
      </h1>
      {children}
    </div>
  </main>
);

const volverAlPortal = () => window.location.replace(urlDelPortal("/"));

const EntrarDesdeZoco = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const sesionAnterior = useSelector((s) => s.auth);

  const [estado, setEstado] = useState("canjeando"); // "canjeando" | "sin_panel" | "error"
  const [mensaje, setMensaje] = useState("");
  const [negocioNombre, setNegocioNombre] = useState("");

  // Una sola vez, aunque React monte el componente dos veces en desarrollo: el segundo canje del
  // mismo código responde "ya se usó" y pisaría la sesión buena con un error.
  const canjeado = useRef(false);

  useEffect(() => {
    if (canjeado.current) return;
    canjeado.current = true;

    // Regla 5 de 03-seguridad: cambiar de negocio no reusa el token viejo. La sesión que ya
    // tenía esta pestaña —otro negocio, o un login propio de Tickets— se cierra ANTES del canje,
    // así, si el canje falla, la pestaña no queda parada en silencio en el negocio anterior.
    if (sesionAnterior?.isAuthenticated) dispatch({ type: LOGOUT_SUCCESS });

    const codigo = new URLSearchParams(location.search).get("codigo");
    if (!codigo) {
      setMensaje("Falta el código de entrada. Volvé a entrar desde el portal de ZOCO.");
      setEstado("error");
      return;
    }

    (async () => {
      try {
        const res = await zocoCanjearApi(codigo);
        const { token, refreshToken, cuenta, negocio, perfil: perfilDelCanje } = res.data.info;
        const iniciarSesion = (role, extra = {}) =>
          dispatch({
            type: LOGIN_SUCCESS,
            payload: {
              accessToken: token,
              refreshToken,
              user: { id: cuenta.id, email: cuenta.email, name: cuenta.nombre, role, ...extra },
              origen: ORIGEN_ZOCO,
              negocio,
            },
          });

        // USER hasta que el back diga otra cosa: es lo que la sesión es para cualquier ruta que
        // no sea del panel.
        iniciarSesion("USER");

        if (!negocio) {
          navigate("/event", { replace: true });
          return;
        }

        // El canje ya viene con la respuesta: el perfil del panel, o null si esta persona no
        // entra. Un viaje menos, que desde Argentina son ~271 ms contra el mismo back que acaba
        // de contestar. `undefined` es un back viejo que todavia no lo manda; ahi se pregunta
        // como antes, para que el orden en que se desplieguen los dos no importe.
        let perfil = perfilDelCanje;

        if (perfil === undefined) {
          try {
            const meRes = await organizerMeApi();
            perfil = meRes?.data?.info || {};
          } catch (e) {
            if (e?.response?.status !== 403) throw e;
            perfil = null;
          }
        }

        if (!perfil) {
          setNegocioNombre(negocio.nombre);
          setEstado("sin_panel");
          return;
        }

        // tipo/perms.tickets deciden, en Layout.jsx y routes.js, qué navegación y qué rutas ve un
        // empleado (organizer-permission-guards). El dueño no tiene perms y ve todo, como siempre.
        iniciarSesion("ORGANIZER", { tipo: perfil.tipo ?? null, perms: perfil.perms ?? null });

        // El portal puede mandar a un módulo puntual (eventos, mesas, canchas) en `volver`.
        // Si no vino o no es una ruta de acá, el panel, que es donde se entraba siempre.
        const destino = rutaDeVuelta(new URLSearchParams(location.search).get("volver"));
        navigate(destino ?? "/organizer", { replace: true });
      } catch (e) {
        setMensaje(e?.response?.data?.message || "No se pudo entrar. Probá de nuevo desde el portal de ZOCO.");
        setEstado("error");
      }
    })();
    // Corre una vez por carga de la página: el código es de un solo uso.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (estado === "error") {
    return (
      <Marco>
        <div className="entrar-zoco__icono-error" aria-hidden="true">
          !
        </div>
        <h2 className="entrar-zoco__titulo">No pudimos entrar</h2>
        <p className="entrar-zoco__texto" role="alert">
          {mensaje}
        </p>
        {/* El código ya se gastó: el portal emite otro con el negocio elegido. */}
        <div className="entrar-zoco__acciones">
          <button type="button" className="entrar-zoco__boton entrar-zoco__boton--primario" onClick={volverAlPortal}>
            Volver al portal de ZOCO
          </button>
        </div>
      </Marco>
    );
  }

  if (estado === "sin_panel") {
    return (
      <Marco>
        <h2 className="entrar-zoco__titulo">Tu rol no entra al panel</h2>
        <p className="entrar-zoco__texto" role="alert">
          Entraste a <strong>{negocioNombre}</strong>, pero tu rol todavía no entra al panel de ZOCO Tickets. Por ahora el
          panel lo opera el dueño del negocio.
        </p>
        <div className="entrar-zoco__acciones">
          <button
            type="button"
            className="entrar-zoco__boton entrar-zoco__boton--primario"
            onClick={() => navigate("/event", { replace: true })}
          >
            Ir a ZOCO Tickets
          </button>
          <button type="button" className="entrar-zoco__boton entrar-zoco__boton--secundario" onClick={volverAlPortal}>
            Volver al portal de ZOCO
          </button>
        </div>
      </Marco>
    );
  }

  return (
    <Marco>
      <div className="entrar-zoco__spinner" aria-hidden="true" />
      <div role="status">
        <p className="entrar-zoco__titulo">Entrando con tu cuenta de ZOCO…</p>
        <p className="entrar-zoco__texto">Esto tarda solo unos segundos.</p>
      </div>
    </Marco>
  );
};

export default EntrarDesdeZoco;
